/**
 * The @mention note editor, in one place.
 *
 * Two composers now write notes that can tag a colleague — the one on a record's activity panel
 * and the one in the Needs Your Attention widget — and they have to agree about three things: the
 * markup a mention is stored as, how the autocompleter is registered, and how the ids are read
 * back out. A second copy of that config is a second thing to keep in step, and the failure would
 * be quiet: a mention that looks right and assigns nobody.
 */

/**
 * The span a mention is stored as. The id is on the element rather than inferred from the name,
 * so saving reports exactly who was tagged instead of matching text back to people.
 */
export const mentionMarkup = (user) => '<span class="note-mention" data-mention-id="' + user.id + '">@' + user.name + '</span>&nbsp;';

/** Which people a body tags, read from the markup above. */
export const mentionsIn = (html) => {
	const ids = [];
	const re = /data-mention-id="(\d+)"/g;
	let match;

	while ((match = re.exec(html || '')) !== null) {
		const id = Number(match[1]);
		if (!ids.includes(id)) ids.push(id);
	}

	return ids;
};

/**
 * A TinyMCE config with the mention autocompleter wired in.
 *
 * `users` is a function rather than an array because the list usually arrives after the editor is
 * built — the caller would otherwise capture an empty one and never see the people.
 */
export const mentionEditorInit = (options: {
	users: () => any[];
	placeholder?: string;
	toolbar?: any;
	minHeight?: number;
	maxHeight?: number;
	onSetup?: (editor: any) => void;
}) => ({
		base_url: '/tinymce',
		suffix: '.min',
		menubar: false,
		statusbar: false,
		plugins: 'lists autoresize',
		toolbar: options.toolbar ?? false,
		min_height: options.minHeight ?? 140,
		max_height: options.maxHeight ?? 320,
		autoresize_bottom_margin: 0,
		placeholder: options.placeholder ?? 'Take a note and @person',
		extended_valid_elements: 'span[class|data-mention-id]',

		setup: (editor) => {
			if (options.onSetup) options.onSetup(editor);

			/*
			 * Close the list when something scrolls under it.
			 *
			 * TinyMCE places the autocompleter once, with showAt, into `.tox-tinymce-aux` — a
			 * position:relative sink on <body> — so its coordinates are the document's, and 5.10
			 * binds no scroll handler to revisit them. On an ordinary page that is invisible:
			 * scrolling the window moves the editor and the list together. The CRM scrolls an
			 * inner panel instead, so the caret travels and the list stays where it was.
			 *
			 * **Following the caret was tried twice and is not here.** Recomputing from
			 * editor.selection.getBoundingClientRect() measures inside the iframe, and comparing
			 * that against page coordinates dismissed the list on every scroll. Moving it by the
			 * editor's own delta needs the position TinyMCE gave it, and reading that back from
			 * the inline style gave 0 when the style was not set the way the code assumed — which
			 * threw the list to the top of the page. Both are the same mistake: guessing at the
			 * popup's coordinate space without being able to watch it.
			 *
			 * So it closes, with the Escape TinyMCE itself binds for dismissal. That is standard
			 * behaviour in every other editor, and a closed list is never in the wrong place.
			 */
			editor.on('init', () => {
				const doc = editor.getContainer()?.ownerDocument;
				if (!doc) return;

				const close = () => {
					if (!doc.querySelector('.tox-autocompleter')) return;
					editor.getBody()?.dispatchEvent(new KeyboardEvent('keydown', {
						key: 'Escape', keyCode: 27, which: 27, bubbles: true
					}));
				};

				// Capture, because scroll does not bubble: a listener on the document hears an
				// inner panel scrolling only on the way down.
				doc.addEventListener('scroll', close, true);
				editor.on('remove', () => doc.removeEventListener('scroll', close, true));
			});

			editor.ui.registry.addAutocompleter('crm-mention', {
				// `ch` is what TinyMCE 5 reads — `trigger` is the 6.x name, which 5.10 stores
				// without complaint and then never matches. Both are given so an upgrade cannot
				// silently break it back.
				ch: '@',
				trigger: '@',
				minChars: 0,
				columns: 1,

				fetch: (pattern) => {
					const term = (pattern || '').toLowerCase();
					return Promise.resolve((options.users() || [])
						.filter(u => !term || (u.name || '').toLowerCase().includes(term))
						.map(u => ({ type: 'autocompleteitem', value: String(u.id), text: u.name })));
				},

				onAction: (api, range, value) => {
					const user = (options.users() || []).find(u => String(u.id) === String(value));
					if (user) {
						// Replace the typed "@…" itself, not just insert beside it.
						editor.selection.setRng(range);
						editor.insertContent(mentionMarkup(user));
					}
					api.hide();
				}
			});
		}
	});
